import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Container, Card, CardContent, Button } from "@kmf/ui";
import { newsApi, type News } from "@/lib/api";

const ITEMS_PER_PAGE = 10;

export function NewsListPage() {
  const [news, setNews] = useState<News[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    document.title = "뉴스 - KMF 할랄위원회";
    loadNews();
  }, []);

  const loadNews = async () => {
    try {
      const data = await newsApi.list();
      setNews(data);
    } catch (error) {
      console.error("Failed to load news:", error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(news.length / ITEMS_PER_PAGE);
  const paginatedNews = news.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <div className="public-page py-12 md:py-16">
      <Container>
        {/* Page Header */}
        <div className="public-page-header">
          <h1 className="text-3xl font-bold text-foreground">뉴스</h1>
          <p className="mt-2 text-muted-foreground">
            할랄 산업 관련 언론 기사를 확인하세요
          </p>
        </div>

        {/* News List */}
        {loading ? (
          <div className="py-12 text-center text-muted-foreground">
            로딩 중...
          </div>
        ) : news.length === 0 ? (
          <Card className="public-panel">
            <CardContent className="py-12 text-center text-muted-foreground">
              등록된 뉴스가 없습니다.
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="publication-list space-y-3">
              {paginatedNews.map((item) => (
                <Link
                  key={item.NewsID}
                  to={`/news/${item.NewsID}`}
                  className="block"
                >
                  <Card className="public-panel transition-colors hover:bg-accent">
                    <CardContent className="p-4 md:p-6">
                      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                        <div className="flex-1">
                          <span className="publication-type inline-block rounded bg-secondary/10 px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                            언론기사
                          </span>
                          <h2 className="mt-2 font-medium text-foreground line-clamp-1">
                            {item.Title}
                          </h2>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {item.UpdateDateTime}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination mt-8 flex justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  이전
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </Button>
                  )
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                >
                  다음
                </Button>
              </div>
            )}
          </>
        )}
      </Container>
    </div>
  );
}
